import { api } from "./api";
import { setOrbLevel, setOrbState } from "./orb";
import { logWarn } from "./log";

let ctx = null;
let current = null;

export function getAudioCtx() {
  if (!ctx) {
    const C = window.AudioContext || window.webkitAudioContext;
    ctx = new C();
  }
  return ctx;
}

export function unlockAudio() {
  try { getAudioCtx().resume(); } catch (e) { logWarn("Operazione non riuscita", e); }
}

// Synthesized heartbeat "thump" (low sine with fast decay).
export function thump(strength = 1) {
  if (!ctx || ctx.state !== "running") return;
  const t = ctx.currentTime;
  [[70, 38, 0.9], [140, 80, 0.25]].forEach(([f0, f1, vol]) => {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.setValueAtTime(f0, t);
    o.frequency.exponentialRampToValueAtTime(f1, t + 0.16);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(vol * strength, t + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.24);
    o.connect(g);
    g.connect(ctx.destination);
    o.start(t);
    o.stop(t + 0.28);
  });
}

export function stripMarkdown(t) {
  return (t || "")
    .replace(/\n+Fonti:[\s\S]*$/, "")
    .replace(/```[\s\S]*?```/g, " (codice) ")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/\[\d+\]/g, "")
    .replace(/https?:\/\/\S+/g, "")
    .replace(/[#>*_~|]/g, "")
    .replace(/^\s*[-+]\s+/gm, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 3800);
}

const fetchTts = (text, voice) => api.post("/tts", { text, voice }, { responseType: "blob" }).then((r) => r.data);

export function stopSpeaking() {
  if (current) {
    current.pause();
    current.onended && current.onended();
    current = null;
  }
}

// Plays one audio blob while driving the orb with real amplitude. Resolves when finished.
function playBlob(blob) {
  const url = URL.createObjectURL(blob);
  const audio = new Audio(url);
  audio.crossOrigin = "anonymous";
  const ac = getAudioCtx();
  let analyser = null;
  try {
    const src = ac.createMediaElementSource(audio);
    analyser = ac.createAnalyser();
    analyser.fftSize = 256;
    src.connect(analyser);
    analyser.connect(ac.destination);
  } catch (e) { logWarn("Analizzatore audio non disponibile", e); }
  current = audio;
  return new Promise((resolve, reject) => {
    let raf;
    const buf = analyser ? new Uint8Array(analyser.frequencyBinCount) : null;
    const loop = () => {
      if (analyser) {
        analyser.getByteFrequencyData(buf);
        let s = 0;
        for (let i = 0; i < buf.length; i++) s += buf[i];
        setOrbLevel(Math.min(1, (s / buf.length / 255) * 2.4));
      } else setOrbLevel(0.4 + Math.random() * 0.4);
      raf = requestAnimationFrame(loop);
    };
    const end = () => {
      cancelAnimationFrame(raf);
      setOrbLevel(0);
      URL.revokeObjectURL(url);
      if (current === audio) current = null;
      audio.onended = null;
      resolve();
    };
    audio.onended = end;
    audio.onerror = end;
    ac.resume().catch(() => {});
    const guard = setTimeout(() => { if (audio.paused) end(); }, 6000);
    audio.play().then(() => { clearTimeout(guard); setOrbState("parlato"); loop(); }).catch((e) => {
      clearTimeout(guard);
      if (current === audio) current = null;
      reject(e);
    });
  });
}

export async function speak(text, voice) {
  stopSpeaking();
  const clean = stripMarkdown(text);
  if (!clean) return;
  try { await playBlob(await fetchTts(clean, voice)); } finally { setOrbState("idle"); }
}
